import React from 'react'
import gql from 'graphql-tag'
import memoize from 'lodash.memoize'
import { css } from '@emotion/css'
import { AttackEvent, attackEventFragment } from './shared'
import { AggregationOptions, AggregationConfigs } from '../aggregations'
import { OrderFieldConfig, Order } from '../../legacy-design-system/order'
import { GridRowQuery } from './grid-row-query'
import {
  OnGroupToggleProps,
  OnOptionToggleProps
} from '../../legacy-design-system/filter'
import { OrderDirection } from '../../graphql/schema/types/order-direction'
import { Grid, MaxCellHeightsByRow } from '../../legacy-design-system/grid'
import { GridLoader, GridStateManager } from '../grid-data'
import { LegacyGridContainer } from '../../legacy-design-system/legacy-main'
import { BulkActionList } from './bulk-action-list'
import { LoadingTextMask } from '../../legacy-design-system/loading-masks'
import { ResultCell } from './cell-content/result-cell'
import { AttackerCell } from './cell-content/attacker-cell'
import { ReceivedTimeCell } from './cell-content/received-time-cell'
import { TargetCell } from './cell-content/target-cell'
import { AttackTypeCell } from './cell-content/attack-type-cell'
import { FeedbackButton } from '../../legacy-design-system/grid/feedback'
import { GridToggle } from '../../legacy-design-system/grid/grid-toggle'
import { AttackEventsGridDrilldownPanel } from './drilldown-panel/drilldown-panel'
import { getAttackEventRowActionsCellRenderer } from './actions/row-actions-cell'
import { GridActiveRowManager } from '../../legacy-design-system/grid-active-row-manager'
import { sendDefaultEvent } from '../analytics'
import { DrilldownContent } from './drilldown-panel/drilldown-content'

const defaultRowHeight = 90
const attackEventsBatchSize = 25

export const attackEventsPageQuery = gql`
  query AttackEventsPage(
    $offset: Int!
    $limit: Int!
    $organizationID: ID!
    $orderBy: AttackEventOrder!
    $filterText: String
    $quickFilter: String
    $applications: [String]
    $attackers: [String]
    $environments: [String]
    $results: [String]
    $rules: [String]
    $servers: [String]
    $severities: [String]
    $tags: [String]
    $sourceNames: [String]
    $startDate: String
    $endDate: String
    $includeBotBlockers: Boolean
    $includeIpBlacklist: Boolean
    $includeSuppressed: Boolean
  ) {
    attackEvents(
      offset: $offset
      limit: $limit
      organizationID: $organizationID
      orderBy: $orderBy
      filters: {
        filterText: $filterText
        quickFilter: $quickFilter
        applications: $applications
        attackers: $attackers
        environments: $environments
        results: $results
        rules: $rules
        servers: $servers
        severities: $severities
        tags: $tags
        sourceNameIDs: $sourceNames
        startDate: $startDate
        endDate: $endDate
        includeBotBlockers: $includeBotBlockers
        includeIpBlacklist: $includeIpBlacklist
        includeSuppressed: $includeSuppressed
      }
    ) {
      items {
        ...AttackEvent
      }
      total
    }
  }

  ${attackEventFragment}
`

const attackEventAggregationsQuery = gql`
  query AttackEventsAggregations(
    $organizationID: ID!
    $filterText: String
    $quickFilter: String
    $applications: [String]
    $attackers: [String]
    $environments: [String]
    $results: [String]
    $rules: [String]
    $servers: [String]
    $severities: [String]
    $sourceNames: [String]
    $tags: [String]
    $startDate: String
    $endDate: String
    $includeBotBlockers: Boolean
    $includeIpBlacklist: Boolean
    $includeSuppressed: Boolean
  ) {
    attackEventAggregations(
      organizationID: $organizationID
      filters: {
        filterText: $filterText
        quickFilter: $quickFilter
        applications: $applications
        attackers: $attackers
        environments: $environments
        results: $results
        rules: $rules
        servers: $servers
        sourceNameIDs: $sourceNames
        severities: $severities
        tags: $tags
        startDate: $startDate
        endDate: $endDate
        includeBotBlockers: $includeBotBlockers
        includeIpBlacklist: $includeIpBlacklist
        includeSuppressed: $includeSuppressed
      }
    ) {
      applications {
        count
        key {
          id
          label
        }
      }
      attackers {
        count
        key
      }
      environments {
        count
        key
      }
      results {
        count
        key {
          id
          label
        }
      }
      rules {
        count
        key {
          id
          label
        }
      }
      servers {
        count
        key {
          id
          label
        }
      }
      severities {
        count
        key
      }
      tags {
        count
        key
      }
      sourceNames {
        count
        key {
          id
          label
        }
      }
    }
  }
`

const attackEventQuickFiltersQuery = gql`
  query AttackEventsQuickFilters(
    $organizationID: ID!
    $filterText: String
    $quickFilter: String
    $applications: [String]
    $attackers: [String]
    $environments: [String]
    $results: [String]
    $rules: [String]
    $servers: [String]
    $severities: [String]
    $tags: [String]
    $sourceNames: [String]
    $startDate: String
    $endDate: String
    $includeBotBlockers: Boolean
    $includeIpBlacklist: Boolean
    $includeSuppressed: Boolean
  ) {
    attackEventQuickFilters(
      organizationID: $organizationID
      filters: {
        filterText: $filterText
        quickFilter: $quickFilter
        applications: $applications
        attackers: $attackers
        environments: $environments
        results: $results
        rules: $rules
        servers: $servers
        severities: $severities
        tags: $tags
        startDate: $startDate
        sourceNameIDs: $sourceNames
        endDate: $endDate
        includeBotBlockers: $includeBotBlockers
        includeIpBlacklist: $includeIpBlacklist
        includeSuppressed: $includeSuppressed
      }
    ) {
      id
      name
      count
    }
  }
`

const initialAggregationSelections: AggregationOptions<AttackEventsAggregationCategory> =
  {
    tags: [],
    attackers: [],
    sourceNames: []
  }

type AttackEventsOrderField = 'SOURCE_NAME'

type AttackEventsAggregationCategory = 'tags' | 'attackers' | 'sourceNames'

const aggregationConfigs: AggregationConfigs<AttackEventsAggregationCategory> =
  {
    tags: {
      groupName: {
        id: 'title_tags'
      }
    },
    attackers: {
      groupName: {
        id: 'title_attackEventsGrid_attackers'
      }
    },
    sourceNames: {
      groupName: {
        id: 'title_attackEventsGrid_sourceNames'
      },
      bucketToID: ({ key: { id } }) => id,
      bucketToString: ({ key: { label } }) => label
    }
  }

export const orderFieldConfig: OrderFieldConfig<AttackEventsOrderField> = {
  SOURCE_NAME: {
    direction: 'ASC',
    message: { id: 'title_attackEventsGrid_sourceName' }
  }
}

const getAttackEventQuery = memoize(
  (organizationID) =>
    ({
      rowDataID,
      children
    }: {
      readonly rowDataID: string
      readonly children: (rowData: AttackEvent) => React.ReactNode
    }) =>
      (
        <GridRowQuery
          organizationID={organizationID}
          rowDataID={rowDataID}
          children={children}
        />
      )
)

function quickFilterToGroup() {
  return 'other'
}

export interface ContextValue {
  readonly order: Order<AttackEventsOrderField>
  readonly filterText: string
  readonly quickFilterID: string
  readonly onSetSortField: (field: AttackEventsOrderField) => void
  readonly onSetSortDirection: (direction: OrderDirection) => void
  readonly onFilterTextChange: (vaslue: string) => void
  readonly onQuickFilterIDChange: (quickFilterID: string) => void
  readonly onGroupToggle: (
    props: OnGroupToggleProps<AttackEventsAggregationCategory>
  ) => void
  readonly onOptionToggle: (
    props: OnOptionToggleProps<AttackEventsAggregationCategory>
  ) => void
  readonly aggregationSelections: AggregationOptions<AttackEventsAggregationCategory>
  readonly onAggregationCategoriesClear: (
    categories: AttackEventsAggregationCategory[]
  ) => void
  readonly updateCellHeight: (rowDataID: string, height: number) => void
  readonly registerListRef: (ref: any) => void
  readonly maxCellHeightsByRow: MaxCellHeightsByRow
  readonly recomputeRowHeights: () => void
  readonly resetFilters: () => void
  readonly rootScope?: {
    readonly $on: (eventName: string, callback: any) => void
  }
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

const AttackEventsGridContext = React.createContext<ContextValue>({
  order: {
    field: 'SOURCE_NAME',
    direction: orderFieldConfig.SOURCE_NAME.direction
  },
  onSetSortField: noop,
  onSetSortDirection: noop,
  filterText: '',
  onFilterTextChange: noop,
  quickFilterID: 'ALL',
  onQuickFilterIDChange: noop,
  aggregationSelections: initialAggregationSelections,
  onGroupToggle: noop,
  onOptionToggle: noop,
  onAggregationCategoriesClear: noop,
  updateCellHeight: noop,
  registerListRef: noop,
  maxCellHeightsByRow: {},
  recomputeRowHeights: noop,
  resetFilters: noop,
  rootScope: { $on: noop }
})

export function AttackEventsGridProvider({
  children,
  rootScope
}: {
  readonly rootScope?: {
    readonly $on: (eventName: string, callback: any) => void
  }
  readonly children: React.ReactNode
}) {
  return (
    <GridStateManager
      defaultOrderField="SOURCE_NAME"
      orderFieldConfig={orderFieldConfig}
      filterType="applications"
    >
      {(state) => {
        return (
          <AttackEventsGridContext.Provider
            value={{
              ...state,
              rootScope
            }}
          >
            {children}
          </AttackEventsGridContext.Provider>
        )
      }}
    </GridStateManager>
  )
}

export function DefaultLoadingCell() {
  return (
    <span>
      <LoadingTextMask min={2} max={7} />
    </span>
  )
}

export function AttackEventsGrid() {
  return (
    <AttackEventsGridContext.Consumer>
      {({
        order,
        onSetSortField,
        onSetSortDirection,
        filterText,
        onFilterTextChange,
        quickFilterID,
        onQuickFilterIDChange,
        aggregationSelections,
        onGroupToggle,
        onOptionToggle,
        onAggregationCategoriesClear,
        updateCellHeight,
        registerListRef,
        maxCellHeightsByRow,
        recomputeRowHeights
      }) => {
        return (
          <GridLoader<
            AttackEvent,
            AttackEventsOrderField,
            AttackEventsAggregationCategory
          >
            aggregationsQuery={attackEventAggregationsQuery}
            aggregationsQueryRootKey="attackEventAggregations"
            batchSize={attackEventsBatchSize}
            quickFiltersQuery={attackEventQuickFiltersQuery}
            quickFiltersQueryRootKey="attackEventQuickFilters"
            collectionQuery={attackEventsPageQuery}
            collectionQueryRootKey="attackEvents"
            filterText={filterText}
            order={order}
            quickFilterID={quickFilterID}
            aggregationBucketIDConfigs={aggregationConfigs}
            aggregationFilters={aggregationSelections}
          >
            {({
              totalRowCount,
              loadMore,
              organizationID,
              organizationType,
              quickFilters,
              aggregations,
              loadedRowCount,
              rowDataValue,
              rowGetter,
              isRowLoaded,
              protectLicenseBreakdown,
              refresh,
              refetchQueries,
              refreshGridData
            }) => {
              const isFreemium = organizationType === 'Freemium'

              const AttackEventQueryMemoized =
                getAttackEventQuery(organizationID)
              return (
                <GridActiveRowManager<string>>
                  {({
                    hasActiveRowID,
                    activeRowID,
                    clearActiveRowID,
                    updateActiveRowID
                  }) => (
                    <LegacyGridContainer>
                      <AttackEventsGridDrilldownPanel
                        organizationID={organizationID}
                        eventUuid={activeRowID}
                        onClose={clearActiveRowID}
                        isOpen={hasActiveRowID}
                        renderBody={() => {
                          if (
                            hasActiveRowID &&
                            typeof activeRowID !== 'undefined'
                          ) {
                            return (
                              <DrilldownContent
                                layout="panel"
                                organizationID={organizationID}
                                eventUuid={activeRowID}
                              />
                            )
                          }
                          return null
                        }}
                      />
                      <Grid<
                        AttackEvent,
                        AttackEventsOrderField,
                        AttackEventsAggregationCategory
                      >
                        updateCellHeight={updateCellHeight}
                        registerListRef={registerListRef}
                        maxCellHeightsByRow={maxCellHeightsByRow}
                        RowQuery={AttackEventQueryMemoized}
                        loadedRowCount={loadedRowCount}
                        rowDataValue={rowDataValue}
                        isRowLoaded={isRowLoaded}
                        rowGetter={rowGetter}
                        loadMore={loadMore}
                        totalRowCount={totalRowCount}
                        rowHeight={(attackEvent) => {
                          if (attackEvent && attackEvent.id) {
                            return (
                              maxCellHeightsByRow[attackEvent.id] ||
                              defaultRowHeight
                            )
                          }
                          return defaultRowHeight
                        }}
                        recomputeRowHeights={recomputeRowHeights}
                        renderBulkActions={(rowSelectionProps) => (
                          <BulkActionList
                            organizationID={organizationID}
                            {...rowSelectionProps}
                          />
                        )}
                        titleMessage={{
                          id: 'title_attackEvents',
                          defaultMessage: 'Attack Events'
                        }}
                        searchPlaceholderMessage={{
                          id: 'placeholder_findAttackEvent',
                          defaultMessage: 'Find Attack Event'
                        }}
                        order={order}
                        onSetSortField={(value) => {
                          onSetSortField(value)
                          sendDefaultEvent('attacks.events.grid.sort', value)
                        }}
                        onSetSortDirection={onSetSortDirection}
                        orderFieldConfig={orderFieldConfig}
                        onFilterTextChange={(value) => {
                          onFilterTextChange(value)
                          sendDefaultEvent('attacks.events.grid.search', value)
                        }}
                        filterText={filterText}
                        onQuickFilterIDChange={onQuickFilterIDChange}
                        quickFilterID={quickFilterID}
                        quickFilters={quickFilters}
                        quickFilterToGroup={quickFilterToGroup}
                        aggregations={aggregations}
                        aggregationConfigs={aggregationConfigs}
                        aggregationSelections={aggregationSelections}
                        onGroupToggle={onGroupToggle}
                        onOptionToggle={onOptionToggle}
                        onAggregationCategoriesClear={
                          onAggregationCategoriesClear
                        }
                        batchSize={attackEventsBatchSize}
                        gridDataValue={{
                          protectLicenseBreakdown,
                          activeEventUuid: activeRowID
                        }}
                        headerExtraChildren={
                          <React.Fragment>
                            <FeedbackButton />
                            <GridToggle />
                          </React.Fragment>
                        }
                        columns={[
                          {
                            cellRenderer: ({
                              rowData: { result },
                              getCellContentProps
                            }) => {
                              return (
                                <div {...getCellContentProps({})}>
                                  <ResultCell rowData={{ result }} />
                                </div>
                              )
                            },
                            loadingCellRenderer: () => <DefaultLoadingCell />,
                            headerConfig: {
                              message: {
                                id: 'title_attackEventGrid_columnHeader_result',
                                defaultMessage: 'Result'
                              },
                              aggregations: []
                            },
                            width: 150,
                            minWidth: 100,
                            grow: 5,
                            horizontalCenter: false
                          },
                          {
                            cellRenderer: ({
                              rowData: {
                                id,
                                sourceIP,
                                attackType,
                                attackers,
                                sourceName,
                                tags
                              },
                              getCellContentProps
                            }) => {
                              return (
                                <div
                                  {...getCellContentProps({
                                    className: css`
                                      padding-right: 20px;
                                      overflow: hidden;
                                    `
                                  })}
                                >
                                  <AttackerCell
                                    isActiveEvent={activeRowID === id}
                                    onAttackerClick={() => {
                                      updateActiveRowID(id)
                                    }}
                                    rowData={{
                                      sourceIP,
                                      attackType,
                                      attackers,
                                      sourceName: sourceName
                                        ? sourceName.name
                                        : undefined,
                                      tags
                                    }}
                                  />
                                </div>
                              )
                            },
                            loadingCellRenderer: () => <DefaultLoadingCell />,
                            headerConfig: {
                              message: {
                                id: 'title_attackEventGrid_columnHeader_attacker',
                                defaultMessage: 'Attacker'
                              },
                              aggregations: ['tags', 'attackers', 'sourceNames']
                            },
                            width: 270,
                            minWidth: 170,
                            grow: 5,
                            horizontalCenter: false
                          },
                          {
                            cellRenderer: ({
                              rowData: { displayName, id, type, userInput },
                              getCellContentProps
                            }) => {
                              return (
                                <div
                                  {...getCellContentProps({})}
                                  className={css`
                                    max-width: 100%;
                                  `}
                                >
                                  <AttackTypeCell
                                    organizationID={organizationID}
                                    rowData={{
                                      displayName,
                                      id,
                                      type,
                                      userInput
                                    }}
                                  />
                                </div>
                              )
                            },
                            loadingCellRenderer: () => <DefaultLoadingCell />,
                            headerConfig: {
                              message: {
                                id: 'title_attackType'
                              },
                              aggregations: []
                            },
                            width: 270,
                            minWidth: 170,
                            grow: 3,
                            horizontalCenter: false
                          },
                          {
                            cellRenderer: ({
                              rowData: { received },
                              getCellContentProps
                            }) => {
                              return (
                                <div {...getCellContentProps({})}>
                                  <ReceivedTimeCell rowData={{ received }} />
                                </div>
                              )
                            },
                            loadingCellRenderer: () => <DefaultLoadingCell />,
                            headerConfig: {
                              message: {
                                id: 'title_attackEventGrid_columnHeader_time',
                                defaultMessage: 'Time'
                              },
                              aggregations: []
                            },
                            width: 270,
                            minWidth: 170,
                            grow: 5,
                            horizontalCenter: false
                          },
                          {
                            cellRenderer: ({
                              rowData: { application, server, url },
                              getCellContentProps
                            }) => {
                              return (
                                <div {...getCellContentProps({})}>
                                  <TargetCell
                                    organizationID={organizationID}
                                    rowData={{ application, server, url }}
                                  />
                                </div>
                              )
                            },
                            loadingCellRenderer: () => <DefaultLoadingCell />,
                            headerConfig: {
                              message: {
                                id: 'title_attackEventGrid_columnHeader_target',
                                defaultMessage: 'Target'
                              },
                              aggregations: []
                            },
                            width: 270,
                            minWidth: 170,
                            grow: 5,
                            horizontalCenter: false
                          },
                          {
                            cellRenderer: getAttackEventRowActionsCellRenderer({
                              refreshGridData,
                              organizationID,
                              refetchQueries,
                              refresh,
                              isFreemium
                            }),
                            loadingCellRenderer: () => <div />,
                            maxWidth: 110,
                            width: 110,
                            minWidth: 110
                          }
                        ]}
                      />
                    </LegacyGridContainer>
                  )}
                </GridActiveRowManager>
              )
            }}
          </GridLoader>
        )
      }}
    </AttackEventsGridContext.Consumer>
  )
}
